export interface CustomError {
  status?: number
  message: string
  data: string
}
type ShowSnackbarType = (
  message: string | null,
  severity: "default" | "error" | "success" | "warning" | "info" | undefined
) => void

const statusHandlers: {
  [key: number]: (showSnackbar: ShowSnackbarType, message?: string) => void
} = {
  401: (showSnackbar: ShowSnackbarType, message?: string) => {
    showSnackbar(message || "Unauthorized. Redirecting to login.", "error")
  },
  524: (showSnackbar: ShowSnackbarType, message?: string) => {
    showSnackbar(message || "Request Timed Out.", "error")
  },
  404: (showSnackbar: ShowSnackbarType, message?: string) => {
    showSnackbar(message || "A server could not find a client-requested API.", "error")
  },
  500: (showSnackbar: ShowSnackbarType, message?: string) => {
    showSnackbar(
      message ||
        "A server encountered an unexpected condition that prevented it from fulfilling the request.",
      "error"
    )
  },
}

export const asyncErrorHandler = <T extends unknown[], R>(
  asyncFunc: (...args: T) => Promise<R>,
  // eslint-disable-next-line @typescript-eslint/ban-types
  showSnackbar: ShowSnackbarType
) => {
  return async (...params: T): Promise<R | void> => {
    try {
      return await asyncFunc(...params)
    } catch (error) {
      const typedError = error as CustomError

      // The API reports business/validation failures (e.g. "Insufficient
      // balance", "Shift [...] not found") as HTTP 500 with a text/plain body,
      // which RTK Query surfaces as `data`. Prefer that over the generic text.
      const serverMessage =
        typedError.message ||
        (typeof typedError.data === "string" && typedError.data.trim()
          ? typedError.data.trim()
          : undefined)

      const handler = typedError.status && statusHandlers[typedError.status]
      if (handler) {
        handler(showSnackbar, serverMessage)
      } else {
        showSnackbar(serverMessage || "Something went wrong!", "error")
      }
    }
  }
}
